import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Typography,
    IconButton
} from '@mui/material';
import { Grid } from '@mui/material';
import {
    Calendar,
    Edit,
    Mail,
    MapPin,
    MessageSquare,
    Package,
    Phone,
    PlusCircle,
    Save,
    Star,
    User,
    X,
    Camera
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from "../../axios/axios-instance";
import api from "../../hooks/api";
import { pathAPI } from "../../utils/pathApi";
import { useCountries } from '../../hooks/useCountries';
import { formatNumber } from '../../utils/utils';
import { useBreadcrumbLabel } from "../../components/breadcrumb/BreadcrumbContext";

type SupportedCountry = {
  id: number;
  code: string;
  name: string;
  country_short_code: string;
  currency: string;
  phone_code: string;
  zone_id: string;
  flag: string;
  language_id: number;
  value: number;
};

type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING' | 'DEACTIVATE';

type AppUser = {
  id: number;
  full_name: string;
  email: string;
  phone_number?: string;
  country?: SupportedCountry;
  created_date?: string;
  total_orders?: number;
  total_order_values?: number;
  total_supported_tickets?: number;
  list_order?: any[];
  support_tickets?: any[];
  status: UserStatus;
  avatar?: string;
};

type AppUserUpdateRequest = {
  fullName: string;
  avatar?: File;
  country?: SupportedCountry;
  status: UserStatus;
};

type LoyaltyPointSummary = {
  availablePoints: number;
  pendingPoints: number;
  lifetimeEarnedPoints: number;
  lifetimeRedeemedPoints: number;
};

type LoyaltyPointTransaction = {
  id: number;
  type: string;
  status: string;
  points: number;
  balanceAfter: number;
  sourceType?: string;
  sourceId?: number;
  orderId?: number;
  outletId?: number;
  serviceId?: number;
  quantity?: number;
  description?: string;
  createdDate?: string;
};

const defaultLoyaltySummary: LoyaltyPointSummary = {
  availablePoints: 0,
  pendingPoints: 0,
  lifetimeEarnedPoints: 0,
  lifetimeRedeemedPoints: 0,
};

const unwrap = (response: any) => response?.data?.data ?? response?.data ?? response;

const unwrapPageItems = (payload: any): LoyaltyPointTransaction[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const unwrapPageTotal = (payload: any, fallback: number) =>
  payload?.total_elements ?? payload?.totalElements ?? payload?.total ?? fallback;

const formatPoints = (value?: number) => formatNumber(value || 0);

function TabPanel({ children, value, index, ...other }: any) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
}

export const UserAppDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [userData, setUserData] = useState<AppUser | null>(null);
  useBreadcrumbLabel(userData?.full_name || userData?.email);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [{ data: countries }] = useCountries();
  const [editedData, setEditedData] = useState<AppUser>({
    id: 0,
    full_name: '',
    email: '',
    phone_number: '',
    country: { id: 0, code: '', name: '', country_short_code: '', currency: '', phone_code: '', zone_id: '', flag: '', language_id: 0, value: 0 },
    created_date: '',
    total_orders: 0,
    total_order_values: 0,
    total_supported_tickets: 0,
    list_order: [],
    support_tickets: [],
    status: 'ACTIVE',
    avatar: ''
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [loyaltySummary, setLoyaltySummary] = useState<LoyaltyPointSummary>(defaultLoyaltySummary);
  const [loyaltyTransactions, setLoyaltyTransactions] = useState<LoyaltyPointTransaction[]>([]);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltyError, setLoyaltyError] = useState("");
  const [loyaltyPage, setLoyaltyPage] = useState(0);
  const [loyaltyRowsPerPage, setLoyaltyRowsPerPage] = useState(20);
  const [loyaltyTotal, setLoyaltyTotal] = useState(0);
  const [adjustmentPoints, setAdjustmentPoints] = useState("");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [adjustmentError, setAdjustmentError] = useState("");
  const [adjustmentSaving, setAdjustmentSaving] = useState(false);

  const userStatuses: UserStatus[] = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'PENDING', 'DEACTIVATE'];

  const displayData = isEditing ? editedData : userData;

  const handleEdit = () => {
    setEditedData(userData || editedData);
    setAvatarFile(null);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      const updateData: AppUserUpdateRequest = {
        fullName: editedData.full_name,
        country: editedData.country,
        status: editedData.status
      };

      if (avatarFile) {
        updateData.avatar = avatarFile;
      }

      const formData = new FormData();
      formData.append('fullName', updateData.fullName);
      if (updateData.country) {
        formData.append('country', JSON.stringify(updateData.country));
      }
      formData.append('status', updateData.status);
      if (updateData.avatar) {
        formData.append('avatar', updateData.avatar);
      }

      const response = await api.PUT(`${pathAPI.appUsers}/${id}`, formData, {}, true);
      
      if (response?.data) {
        setUserData(response.data);
        setEditedData(response.data);
        setIsEditing(false);
        setAvatarFile(null);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedData(userData || editedData);
    setAvatarFile(null);
    setIsEditing(false);
  };

  const handleInputChange = (field: keyof AppUser, value: any) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const fetchLoyalty = async (nextPage = loyaltyPage, nextSize = loyaltyRowsPerPage) => {
    if (!id) return;

    setLoyaltyLoading(true);
    setLoyaltyError("");
    try {
      const [summaryResponse, transactionResponse] = await Promise.all([
        axiosInstance.get(`${pathAPI.appUsers}/${id}/loyalty/summary`),
        axiosInstance.get(`${pathAPI.appUsers}/${id}/loyalty/transactions`, {
          params: {
            page: nextPage,
            size: nextSize,
          },
        }),
      ]);

      const summaryPayload = unwrap(summaryResponse);
      const transactionPayload = unwrap(transactionResponse);
      const items = unwrapPageItems(transactionPayload);

      setLoyaltySummary({ ...defaultLoyaltySummary, ...summaryPayload });
      setLoyaltyTransactions(items);
      setLoyaltyTotal(unwrapPageTotal(transactionPayload, items.length));
    } catch (error) {
      console.error('Error fetching loyalty points:', error);
      setLoyaltyError("Error loading loyalty points");
    } finally {
      setLoyaltyLoading(false);
    }
  };

  const handleChangeLoyaltyPage = (event: unknown, newPage: number) => {
    setLoyaltyPage(newPage);
    fetchLoyalty(newPage, loyaltyRowsPerPage);
  };

  const handleChangeLoyaltyRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextSize = parseInt(event.target.value, 10);
    setLoyaltyRowsPerPage(nextSize);
    setLoyaltyPage(0);
    fetchLoyalty(0, nextSize);
  };

  const handleAdjustPoints = async () => {
    if (!id) return;

    const points = Number(adjustmentPoints);
    const reason = adjustmentReason.trim();
    if (!Number.isInteger(points) || points === 0) {
      setAdjustmentError("Points must be a non-zero integer");
      return;
    }
    if (!reason) {
      setAdjustmentError("Reason is required");
      return;
    }

    setAdjustmentSaving(true);
    setAdjustmentError("");
    try {
      await axiosInstance.post(`${pathAPI.appUsers}/${id}/loyalty/adjustments`, {
        points,
        reason,
      });
      setAdjustmentPoints("");
      setAdjustmentReason("");
      await fetchLoyalty(0, loyaltyRowsPerPage);
      setLoyaltyPage(0);
    } catch (error) {
      console.error('Error adjusting loyalty points:', error);
      setAdjustmentError("Failed to adjust loyalty points");
    } finally {
      setAdjustmentSaving(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return '#ef4444';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        handleInputChange('avatar', result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await api.GET<{ data: AppUser }>(`${pathAPI.appUsers}/${id}`, {}, {}, true);
        if (response?.data) {
          setUserData(response.data);
          setEditedData(response.data);
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  useEffect(() => {
    fetchLoyalty(0, loyaltyRowsPerPage);
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">Error loading user details</Typography>
      </Box>
    );
  }

  return (
    <Card sx={{ margin: '0 auto', p: 3 }}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                sx={{ width: 80, height: 80 }}
                src={displayData?.avatar}
              >
                {!displayData?.avatar && <User size={40} />}
              </Avatar>
              {isEditing && (
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: '#ba2156',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#ba2156',
                      opacity: 0.8
                    },
                    width: 32,
                    height: 32
                  }}
                >
                  <Camera size={16} />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    style={{ display: 'none' }}
                  />
                </IconButton>
              )}
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {displayData?.full_name || 'User Name'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
                {displayData?.email || 'email@example.com'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                User ID: {displayData?.id}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isEditing ? (
              <Button
                variant="contained"
                startIcon={<Edit size={16} />}
                onClick={handleEdit}
                sx={{
                    background: "#ba2156",
                    textTransform:"capitalize",
                    color: "#fff",
                    "&:hover": {
                        background: "#ba2156",
                        color: "#fff",
                    }
                }}
              >
                Edit Profile
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<X size={16} />}
                  onClick={handleCancel}
                  sx={{
                    textTransform:"capitalize",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save size={16} />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{
                    textTransform:"capitalize",
                  }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Main Content */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Personal Information */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <User size={20} />
                    <Typography variant="h6">Personal Information</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Full Name"
                        value={editedData.full_name}
                        onChange={(e) => handleInputChange('full_name', e.target.value)}
                        variant="outlined"
                        size="small"
                        inputProps={{ maxLength: 255 }}
                        helperText={`${editedData.full_name.length}/255 characters`}
                      />
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Full Name
                        </Typography>
                        <Typography variant="body1">{displayData?.full_name || 'N/A'}</Typography>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={editedData.email}
                        disabled
                        variant="outlined"
                        size="small"
                        helperText="Email cannot be edited"
                      />
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Email
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Mail size={16} />
                          <Typography variant="body1">{displayData?.email || 'N/A'}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        label="Phone Number"
                        value={editedData.phone_number}
                        disabled
                        variant="outlined"
                        size="small"
                        helperText="Phone number cannot be edited"
                      />
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Phone Number
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone size={16} />
                          <Typography variant="body1">{displayData?.phone_number || 'N/A'}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    {isEditing ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>Country</InputLabel>
                        <Select
                          value={editedData.country?.id || ''}
                          label="Country"
                          onChange={(e) => {
                            const selectedCountry = countries.find(c => c.id === e.target.value);
                            handleInputChange('country', selectedCountry);
                          }}
                        >
                          {countries.map((country) => (
                            <MenuItem key={country.id} value={country.id}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Country
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MapPin size={16} />
                          <Typography variant="body1">{displayData?.country?.name || 'N/A'}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    {isEditing ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={editedData.status}
                          label="Status"
                          onChange={(e) => handleInputChange('status', e.target.value)}
                        >
                          {userStatuses.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Status
                        </Typography>
                        <Chip
                          label={displayData?.status || 'N/A'}
                          size="small"
                        />
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Created Date
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Calendar size={16} />
                        <Typography variant="body1">
                          {displayData?.created_date ? new Date(displayData.created_date).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Activity Summary */}
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardHeader title={<Typography variant="h6">Account Summary</Typography>} />
              <CardContent>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 6 }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: 'rgba(37, 99, 235, 0.08)',
                        borderRadius: 1
                      }}
                    >
                      <Package size={32} color="#2563eb" style={{ margin: '0 auto 8px' }} />
                      <Typography variant="h4" sx={{ color: '#2563eb', fontWeight: 600 }}>
                        {displayData?.list_order?.length || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Orders
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: 'rgba(22, 163, 74, 0.08)',
                        borderRadius: 1
                      }}
                    >
                      <MessageSquare size={32} color="#16a34a" style={{ margin: '0 auto 8px' }} />
                      <Typography variant="h4" sx={{ color: '#16a34a', fontWeight: 600 }}>
                        {displayData?.support_tickets?.filter(ticket => ticket.status === 'WAITING').length || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Open Tickets
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: 'rgba(217, 119, 6, 0.08)',
                        borderRadius: 1
                      }}
                    >
                      <Typography variant="h4" sx={{ color: '#d97706', fontWeight: 600 }}>
                        ${displayData?.total_order_values?.toFixed(2) || '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Value
                      </Typography>
                    </Box>
                  </Grid>
                    <Grid size={{ xs: 6 }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: 'rgba(190, 24, 93, 0.08)',
                        borderRadius: 1
                      }}
                    >
                      <Typography variant="h4" sx={{ color: '#be185d', fontWeight: 600 }}>
                        {displayData?.total_supported_tickets || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Tickets
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={`Orders (${displayData?.list_order?.length || 0})`} />
            <Tab label={`Support Tickets (${displayData?.support_tickets?.filter(ticket => ticket.status === 'WAITING').length || 0})`} />
            <Tab label={`Loyalty Points (${formatPoints(loyaltySummary.availablePoints)})`} />
          </Tabs>
        </Box>

        {/* Orders Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Order Number</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Items</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData?.list_order?.map((order: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{order.order_number || `Order ${index + 1}`}</TableCell>
                    <TableCell>
                      {order.created_date ? new Date(order.created_date).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status || 'Pending'}
                        size="small"
                        color={order.status === 'Completed' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {order.items ? (
                        typeof order.items === 'string' ? order.items : (
                          <Box>
                            {order.items.service && (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {order.items.service}
                              </Typography>
                            )}
                            {order.items.quantity && (
                              <Typography variant="body2" color="text.secondary">
                                Qty: {order.items.quantity}
                              </Typography>
                            )}
                            {order.items.stylist && (
                              <Typography variant="body2" color="text.secondary">
                                Stylist: {order.items.stylist}
                              </Typography>
                            )}
                            {order.items.note && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Note: {order.items.note}
                              </Typography>
                            )}
                          </Box>
                        )
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell align="right" sx={{fontWeight: 'bold' }}>
                      ${order.total?.toFixed(2) || '0.00'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Support Tickets Tab */}
        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Ticket ID</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Created Date</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Last Modified</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Messages</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData?.support_tickets?.map((ticket: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{ticket.id || `Ticket ${index + 1}`}</TableCell>
                    <TableCell>{ticket.title || 'N/A'}</TableCell>
                    <TableCell>{ticket.description || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.status || 'N/A'}
                        size="small"
                        color={ticket.status === 'WAITING' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {ticket.created_date ? new Date(ticket.created_date).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {ticket.last_modified_date ? new Date(ticket.last_modified_date).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ticket.list_message?.length || 0} messages
                        </Typography>
                        {ticket.list_message?.[0]?.message && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Latest: {ticket.list_message[0].message}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Loyalty Points Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ p: 2, borderRadius: 1, backgroundColor: 'rgba(190, 24, 93, 0.08)' }}>
                <Star size={28} color="#be185d" />
                <Typography variant="h4" sx={{ color: '#be185d', fontWeight: 600, mt: 1 }}>
                  {formatPoints(loyaltySummary.availablePoints)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Available Points</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ p: 2, borderRadius: 1, backgroundColor: 'rgba(37, 99, 235, 0.08)' }}>
                <Typography variant="h4" sx={{ color: '#2563eb', fontWeight: 600 }}>
                  {formatPoints(loyaltySummary.pendingPoints)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Pending Points</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ p: 2, borderRadius: 1, backgroundColor: 'rgba(22, 163, 74, 0.08)' }}>
                <Typography variant="h4" sx={{ color: '#16a34a', fontWeight: 600 }}>
                  {formatPoints(loyaltySummary.lifetimeEarnedPoints)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Lifetime Earned</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ p: 2, borderRadius: 1, backgroundColor: 'rgba(234, 88, 12, 0.08)' }}>
                <Typography variant="h4" sx={{ color: '#ea580c', fontWeight: 600 }}>
                  {formatPoints(loyaltySummary.lifetimeRedeemedPoints)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Lifetime Redeemed</Typography>
              </Box>
            </Grid>
          </Grid>

          <Card sx={{ mb: 3 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlusCircle size={20} />
                  <Typography variant="h6">Adjust User Points</Typography>
                </Box>
              }
            />
            <CardContent>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="Points"
                    type="number"
                    size="small"
                    value={adjustmentPoints}
                    onChange={(event) => setAdjustmentPoints(event.target.value)}
                    helperText="Use negative value to deduct points"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 7 }}>
                  <TextField
                    fullWidth
                    label="Reason"
                    size="small"
                    value={adjustmentReason}
                    onChange={(event) => setAdjustmentReason(event.target.value)}
                    inputProps={{ maxLength: 500 }}
                  />
                  {adjustmentError && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {adjustmentError}
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Save size={16} />}
                    onClick={handleAdjustPoints}
                    disabled={adjustmentSaving}
                    sx={{
                      background: "#ba2156",
                      textTransform: "capitalize",
                      color: "#fff",
                      "&:hover": {
                        background: "#ba2156",
                        color: "#fff",
                      }
                    }}
                  >
                    {adjustmentSaving ? 'Saving...' : 'Save'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {loyaltyError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {loyaltyError}
            </Typography>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Points</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Balance</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Source</TableCell>
                  <TableCell sx={{ backgroundColor: '#ba2156', color: '#fff', fontWeight: 'bold' }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loyaltyLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">Loading loyalty points...</TableCell>
                  </TableRow>
                ) : loyaltyTransactions.length ? (
                  loyaltyTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {transaction.createdDate ? new Date(transaction.createdDate).toLocaleString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.type}
                          size="small"
                          color={transaction.type === 'EARN' ? 'success' : transaction.type === 'ADJUST' ? 'info' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={transaction.status} size="small" />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {transaction.points > 0 ? '+' : ''}{formatPoints(transaction.points)}
                      </TableCell>
                      <TableCell align="right">{formatPoints(transaction.balanceAfter)}</TableCell>
                      <TableCell>
                        {transaction.sourceType || '-'}
                        {transaction.orderId ? ` #${transaction.orderId}` : ''}
                      </TableCell>
                      <TableCell>{transaction.description || '-'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No loyalty point transactions</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={loyaltyTotal}
              page={loyaltyPage}
              onPageChange={handleChangeLoyaltyPage}
              rowsPerPage={loyaltyRowsPerPage}
              onRowsPerPageChange={handleChangeLoyaltyRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </TableContainer>
        </TabPanel>
      </Box>
    </Card>
  );
};
